import {
    FindPeopleRequest,
    IndexedPageView,
    PersonaResponseShape,
    FindPeopleJsonResponse,
    DistinguishedFolderIdName,
    SortResults,
    getEagerService,
} from '../../../service';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';

import {
    HubPersona,
    runOwsRequestWithMonitor,
    createHubPersona,
    Feature,
    fullResponseOperationOption,
    getFindPeopleContext,
    rootContactFolderDistinguishedName,
} from '../../../shared';

const service = getEagerService();

function createfindPeopleRequest(
    folderIdString: string,
    startIndex: number,
    amount: number,
    sortingPath: string
): FindPeopleRequest {
    const defaultPageView: IndexedPageView = {
        BasePoint: 'Beginning',
        Offset: startIndex,
        MaxEntriesReturned: amount,
    };

    const defaultShape: PersonaResponseShape = {
        BaseShape: 'Default',
        AdditionalProperties: [
            service.Factory.propertyUri({ FieldURI: 'PersonaBusinessPhoneNumbers' }),
            service.Factory.propertyUri({ FieldURI: 'PersonaHomePhones' }),
            service.Factory.propertyUri({ FieldURI: 'PersonaMobilePhones' }),
            service.Factory.propertyUri({ FieldURI: 'PersonaDisplayNamePrefix' }),
            service.Factory.propertyUri({ FieldURI: 'PersonaGeneration' }),
            service.Factory.propertyUri({ FieldURI: 'PersonaAttributions' }),
            service.Factory.propertyUri({ FieldURI: 'PersonaTitle' }),

            // Needed for correctly handling/hiding recipient cache email addresses
            service.Factory.propertyUri({ FieldURI: 'PersonaEmails1' }),
            service.Factory.propertyUri({ FieldURI: 'PersonaEmails2' }),
            service.Factory.propertyUri({ FieldURI: 'PersonaEmails3' }),
        ],
    };

    return {
        IndexedPageItemView: service.Factory.indexedPageView(defaultPageView),
        PersonaShape: service.Factory.personaResponseShape(defaultShape),
        QuerySources: ['Mailbox'],
        SearchPeopleSuggestionIndex: false,
        ShouldResolveOneOffEmailAddress: false,
        ParentFolderId: service.Factory.targetFolderId({
            BaseFolderId:
                folderIdString === rootContactFolderDistinguishedName
                    ? service.Factory.distinguishedFolderId({
                          Id: folderIdString as DistinguishedFolderIdName,
                      })
                    : service.Factory.folderId({ Id: folderIdString }),
        }),
        SortOrder: sortOrder(sortingPath),
        Context: getFindPeopleContext(),
    };
}

function sortOrder(sortingPath: string): SortResults[] {
    const primarySort = service.Factory.sortResults({
        Order: 'Ascending',
        Path: service.Factory.propertyUri({ FieldURI: sortingPath }),
    });

    return [primarySort];
}

export default function findPeople(
    folderId: string,
    offset: number,
    limit: number,
    sortingPath: string
): Promise<HubPersona[]> {
    return runOwsRequestWithMonitor({
        scenarioName: 'FindPeople',
        feature: Feature.ContactManagement,
        operation: () =>
            service.Operation.findPeopleOperation(
                service.Factory.findPeopleJsonRequest({
                    Header: getJsonRequestHeader(),
                    Body: createfindPeopleRequest(folderId, offset, limit, sortingPath),
                }),
                fullResponseOperationOption
            ),
        transformResponse: (owsResponse: FindPeopleJsonResponse) =>
            owsResponse.Body.ResultSet.map(createHubPersona),
    });
}
